import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, useMap } from "react-leaflet";
import L, { map } from "leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import './ProjectsMap.css';
import { FaArrowAltCircleRight, FaCircle, FaMapMarkerAlt, FaTruck } from "react-icons/fa";
import ReactDOMServer from 'react-dom/server';
import ProjectAPIs from "../../../../MasonyFixingsAPIs/ProjectAPIs/ProjectAPIs";
import { useToast } from "../../../../Contexts/ToastContext";
import { useNavigate } from "react-router-dom";

// Fix default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const reactIconHtml = ReactDOMServer.renderToString(
  <FaTruck style={{scale: '1.2', background: 'red', borderRadius: '0.5rem', color: "orange", fontSize: "24px", boxShadow: '0px 0px 3px 2px red' }} />
);

const userLocationIcon = L.divIcon({
  className: "", // important: remove default styles
  html: `
    <div class="user-location-marker">
      <div class="pulse"></div>
      <div class="dot"></div>
    </div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const greenIcon = L.icon({
  iconUrl: "/images/marker-green.png",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});

const orangeIcon = L.icon({
  iconUrl: "/images/marker-orange.png",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});


const reactIconMarker = L.divIcon({
  html: reactIconHtml,
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

// Component to handle map events like moveend
const MapEventHandler = ({ onMoveEnd }) => {
  useMapEvents({
    moveend: (e) => {
      onMoveEnd(e.target.getBounds());
    },
  });
  return null;
};


    // const MapController = ({ selectedProject, onMarkerClickRef }) => {
    //     const map = useMap();

    //     useEffect(() => {
    //         if (!selectedProject) return;

    //         const { latitude, longitude } = selectedProject.address || {};
    //         if (!latitude || !longitude) return;

    //         map.flyTo([latitude, longitude], 15, { duration: 0.5 });

    //         // open popup if you want (handled via refs separately)
    //     }, [selectedProject]);

    //     // expose map functions if needed
    //     useEffect(() => {
    //         if (onMarkerClickRef) {
    //         onMarkerClickRef.current = (pos) => {
    //             map.flyTo(pos, 15);
    //         };
    //         }
    //     }, [map]);

    //     return null;
    // };


    const MapController = ({ selectedProjectId, projects, markerRefs }) => {
        const map = useMap();

        useEffect(() => {
            if (!selectedProjectId) return;

            const project = projects.find(p => p.id === selectedProjectId);
            if (!project) return;

            const { latitude, longitude } = project.address || {};
            if (!latitude || !longitude) return;

            const marker = markerRefs.current[selectedProjectId];

            // 🚫 prevent fetch loop if you implemented that earlier
            // if (map._programmaticMoveFlag) {
            // map._programmaticMoveFlag = false;
            // } else {
            // map._programmaticMoveFlag = true;
            // }

            const currentCenter = map.getCenter();
            const distance = currentCenter.distanceTo([latitude, longitude]);

            if (distance > 1000) { // meters
                map.flyTo([latitude, longitude], map.getZoom());
            }

            // const currentZoom = map.getZoom();
            // map.flyTo([latitude, longitude], currentZoom, { duration: 0.5 });

            // ⏳ wait for map animation before opening popup
            setTimeout(() => {
            if (marker) {
                marker.openPopup();
            }
            }, 300);

        }, [selectedProjectId]);


        useEffect(() => {
            const observer = new ResizeObserver(() => {
                map.invalidateSize();
            });

            const container = map.getContainer();
            observer.observe(container);

            return () => observer.disconnect();
        }, [map]);

        return null;
    };


    const FlyToUser = ({ userLocation }) => {
    const map = useMap();
    useEffect(() => {
        if (userLocation) {
        map.flyTo(userLocation, 15);
        }
    }, [userLocation]);
    return null;
    };

    const FlyToUserButton = ({userLocation}) => {
        const map = useMap();
        const ref = useRef();
        
        useEffect(() => {
        if (ref.current) {
            L.DomEvent.disableClickPropagation(ref.current);
        }
        }, []);
        
        const handleClick = () => {
    
            map.flyTo(userLocation, 15, {
                duration: 1.5,
            });
            
        };
        
        return (
            <div
                ref={ref}
                style={{
                position: "absolute",
                bottom: 20,
                right: 20,
                zIndex: 500,
                }}
            >
                <button
                onClick={handleClick}
                style={{
                    padding: "10px 14px",
                    background: "#333",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                }}
                >
                📍 My Location
                </button>
            </div>
        );
    }

    const SetMarkerButton = ({ setMarkerPosition, reverseGeocode, formatAddress, setAddressData }) => {
        const map = useMap();

        const handleClick = async () => {
            const center = map.getCenter();
            const newPos = [center.lat, center.lng];
            setMarkerPosition(newPos);
            const geoData = await reverseGeocode(center.lat, center.lng);
            const formatted = formatAddress(geoData);


            if (formatted) {
                setAddressData(formatted); // 👈 store in state
            }
        };

        return (
            <button
            onClick={handleClick}
            style={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 500,
                padding: "10px 14px",
                background: "#2196f3",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
            }}
            >
            Set Marker at Center
            </button>
        );  
    };



    const SearchBoxWrapper = ({ children }) => {
        const ref = useRef();

        useEffect(() => {
            if (ref.current) {
            L.DomEvent.disableClickPropagation(ref.current);
            }
        }, []);

        return <div ref={ref}>{children}</div>;
    };



const ProjectsMap = ({ editable = false, mode = 'browse', projectAddress, projectId, selectedProjectId, setProjectMapList = () => {}, handleReverseGeocode = ()=>{}, onMarkerClick = ()=>{}} ) => {


    // const map = useMap();
    const mapRef = useRef(null);
    const markerRefs = useRef({});
    const navigate = useNavigate();
    const {addToast} = useToast();
    const [projects, setProjects] = useState([]);
    const [center] = useState([53.33, -6.28]); // Default center (USA)
    const [zoom] = useState(10);

    const [usingCoords, setUsingCoords] = useState(mode === 'create' ? false : true);
    const [userLocation, setUserLocation] = useState(null);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [savedPosition, setSavedPosition] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const [addressData, setAddressData] = useState(null);

    const isChanged = () => {
        if (!markerPosition || !savedPosition) return false;

        return (
            markerPosition[0] !== savedPosition[0] ||
            markerPosition[1] !== savedPosition[1]
        );
    };

    const currentIcon = isChanged() ? null : greenIcon;

    const searchAddress = async (query) => {
        if (!query || query.length < 3) return;

        try {
            const res = await axios.get(
            "https://nominatim.openstreetmap.org/search",
            {
                // params: {
                // q: query,
                // format: "json",
                // addressdetails: 1,
                // limit: 5,
                // },
                params: {
                    q: query,
                    format: "json",
                    countrycodes: "ie,gb",
                    addressdetails: 1,
                    limit: 5,
                },
                headers: {
                "User-Agent": "masonry-project-record", // ⚠️ REQUIRED in production
                },
            }
            );
            setSearchResults(res.data);
        } catch (err) {
            console.error("Search failed:", err);
        }
    };

    const handleSelectLocation = (result) => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        const newPos = [lat, lng];

        setMarkerPosition(newPos);
        setSearchResults([]);

        // Optional:
        // updateCoordinates(lat, lng);
    };

    const reverseGeocode = async (lat, lng) => {
        try {
            const res = await axios.get(
            "https://nominatim.openstreetmap.org/reverse",
            {
                params: {
                lat,
                lon: lng,
                format: "json",
                addressdetails: 1,
                },
                headers: {
                "User-Agent": "masonry-project-record", // required in production
                },
            }
            );
            return res.data;
        } catch (err) {
            console.error("Reverse geocoding failed:", err);
            return null;
        }
    };

    const formatAddress = (data) => {
        if (!data) return null;

        const addr = data.address;

        return {
            line1:
            (addr.house_number ? addr.house_number + ', ' : "")  +(addr.place || addr.office || "") +(addr.road ||
            addr.pedestrian ||
            addr.cycleway ||
            addr.footway ||
            ""),
            city: addr.city || addr.town || addr.city_district || addr.village ||  "",
            county: addr.county || addr.state || addr.region || "",
            country: addr.country || "",
            postcode: addr.postcode || "",
            display: data.display_name,
            longitude: data.lon,
            latitude: data.lat
        };
    };

    const handleUseAddress = () => {
        if(window.confirm('Are you sure you want to autofill this address?')){
            handleReverseGeocode(addressData);
        }
    }

    const handleCreatePin = () => {
        setUsingCoords(true);
        const pinData = {
            line1:"",
            city: "",
            county:"",
            country:"",
            postcode: "",
            display: "",
            longitude: addressData.longitude,
            latitude: addressData.latitude
        };

        handleReverseGeocode(pinData);
    }


  // Fetch projects within map bounds
    const fetchProjects = async (bounds) => {
        if (!bounds) return;
        try {
            const params =  {
            swLat: bounds.getSouthWest().lat,
            swLng: bounds.getSouthWest().lng,
            neLat: bounds.getNorthEast().lat,
            neLng: bounds.getNorthEast().lng,
            }
        const res = await ProjectAPIs.GetProjectsOnMap(params);
        
        setProjects(res.data);
        setProjectMapList(res.data)
        } catch (err) {
        console.error("Failed to fetch projects:", err);
        }
    };


  // Update project coordinates after marker drag
    const updateCoordinates = async (lat, lng) => {
        
        try {
        const response = await ProjectAPIs.PatchProjectCoordinates(projectId, lat, lng);
        addToast({ message: "Coordinates updated successfully!", variant: "success", autoHide: true });
        setSavedPosition(markerPosition);
        } catch (err) {
        console.error("Failed to update coordinates:", err);
        }
    };


    const handleSave = () => {
        if(!markerPosition) return;
        updateCoordinates(markerPosition[0], markerPosition[1]);
    }

    const handleDragEnd = async (e) => {
        const { lat, lng } = e.target.getLatLng();
        setMarkerPosition([lat, lng]);
        const geoData = await reverseGeocode(lat, lng);
        const formatted = formatAddress(geoData);

        setSearchResults([])
        setUsingCoords(false);

        if (formatted) {
            setAddressData(formatted); // 👈 store in state
        }
    };


    const handleClickBrowseMarker = (id, pos) => {
        onMarkerClick(id);

        if (mapRef.current) {
            mapRef.current(pos);
        }
    };

  // Initial fetch when component mounts
    useEffect(() => {
        if(mode === 'browse'){
            fetchProjects()
        }
    }, [mode]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation([position.coords.latitude, position.coords.longitude]);
            },
            (error) => {
                console.warn("Geolocation failed:", error);
            }
            );
        }
    }, []);

    useEffect(() => {
        if (projectAddress?.latitude && projectAddress?.longitude) {
            const pos = [
                projectAddress.latitude,
                projectAddress.longitude,
            ];
            setMarkerPosition(pos);
            setSavedPosition(pos);
        }
    }, [projectAddress]);


    useEffect(() => {
        const delay = setTimeout(() => {
            searchAddress(searchQuery);
        }, 400);

        return () => clearTimeout(delay);
    }, [searchQuery]);

    useEffect(() => {
        if (markerPosition) {
        // map.flyTo(markerPosition, 15, { duration: 1.5 });
        }
    }, [markerPosition]);


    //avoid overuse of reverse geocode
    useEffect(() => {
        if (!markerPosition) return;

        const delay = setTimeout(async () => {
            const [lat, lng] = markerPosition;

            const geoData = await reverseGeocode(lat, lng);
            const formatted = formatAddress(geoData);

            if (formatted) {
            setAddressData(formatted);
            }
        }, 500);

        return () => clearTimeout(delay);
    }, [markerPosition]);


    useEffect(() => {
        if(mode === 'view'){
            setMarkerPosition(savedPosition);
        }
    }, [mode])


  return (
    <div style={{ width: "100%", height: "100%" }}>
        <MapContainer 
            center={mode === "edit" ? (markerPosition || center) : (userLocation || center)}  // center on user if available, fallback otherwise
            zoom={(userLocation || markerPosition) ? 13 : zoom} // closer zoom if user location
            style={{ width: "100%", height: "100%" }}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapController 
                selectedProjectId={selectedProjectId}
                projects={projects}
                markerRefs={markerRefs} 
            />

            {mode === 'browse' && <MapEventHandler onMoveEnd={fetchProjects} />}
            {
            mode !=="browse" ? (markerPosition && <FlyToUser userLocation={markerPosition} />) : 
            (userLocation && <FlyToUser userLocation={userLocation} />)}


            {(mode === 'edit' || mode ==='create') && <SearchBoxWrapper>
                <div style={{
                    position: "absolute",
                    top: 10,
                    left: 50,
                    zIndex: 500,
                    width: "300px"
                }}>
                    <input
                        type="text"
                        placeholder="Search address..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        }}
                    />

                    {searchResults.length > 0 && (
                        <div style={{
                        background: "white",
                        maxHeight: "200px",
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        borderTop: "none"
                        }}>
                        {searchResults.map((result, index) => (
                            <div
                            key={index}
                            onClick={() => handleSelectLocation(result)}
                            style={{
                                padding: "8px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee"
                            }}
                            >
                            {result.display_name}
                            </div>
                        ))}
                        </div>
                    )}
                </div>
            </SearchBoxWrapper>}


            {addressData && (
                <div style={{
                    position: "absolute",
                    bottom: 80,
                    left: 10,
                    zIndex: 500,
                    background: "white",
                    padding: "10px",
                    borderRadius: "6px",
                    width: "300px"
                }}>
                    <strong>Detected Address:</strong>
                    <div>{addressData.display}</div>
                    {(mode === 'edit' || mode === 'create') && <div align='end' className="project-header-actions"><button className="edit-btn" onClick={handleUseAddress}>Use</button></div>}
                </div>
            )}








            {markerPosition && (
                <Marker
                    position={markerPosition}
                    draggable={mode === 'edit' || mode === 'create'}
                    eventHandlers={{
                    dragend: handleDragEnd,
                    }}
                >
                    <Popup>
                    {(isChanged() && (savedPosition !== null)) ? "Unsaved location" : "Saved location"}
                    </Popup>
                </Marker>
            )}

            {(!markerPosition && mode !== 'browse') && 
            <SetMarkerButton 
                setMarkerPosition={setMarkerPosition} 
                reverseGeocode={reverseGeocode} 
                formatAddress={formatAddress}
                setAddressData={setAddressData} 
            />}


            {userLocation && <FlyToUserButton userLocation={userLocation}/>}


            {((markerPosition && isChanged()) || (markerPosition && savedPosition === null)) && (
                <button
                    // onClick={mode !== 'create' ? handleSave : handleCreatePin}
                    onClick={handleCreatePin}
                    disabled={usingCoords}
                    style={{
                    position: "absolute",
                    bottom: 20,
                    left: 20,
                    zIndex: 500,
                    padding: "10px 16px",
                    background: "#e87722",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    }}
                >
                    {usingCoords ? 'Using Current Pin Location' : 'Use Pin Location'}
            </button>
            )}

            {/* {(markerPosition && isChanged()) && (
                <button
                    onClick={() => setMarkerPosition(savedPosition)}
                    style={{
                    position: "absolute",
                    bottom: 20,
                    left: 200,
                    zIndex: 500,
                    padding: "10px 16px",
                    background: "#ff9800",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    }}
                >
                    Cancel
                </button>
            )} */}

            {userLocation && (
                <Marker
                    position={userLocation}
                    icon={userLocationIcon}
                >
                    <Popup>You are here</Popup>
                </Marker>
            )}

            {mode === 'browse' && projects.map((project) => {
                const { latitude, longitude } = project.address || {};
                if (latitude == null || longitude == null) return null;

                return (
                    <Marker
                    key={project.id}
                    position={[latitude, longitude]}
                    draggable={editable}
                    ref={(ref) => {
                        if (ref) markerRefs.current[project.id] = ref;
                    }}
                    eventHandlers={{
                        click: () => {
                            handleClickBrowseMarker(project.id, [latitude, longitude])
                            // onMarkerClick(project.id);
                        },
                        dragend: (e) => editable && handleDragEnd(project.id, e.target),
                    }}
                    >
                            <Popup>
                                <strong>{project.name}</strong>
                                <div>{project.cis_id ? project.cis_id : 'No CIS ID'} | {project.applicant ? project.applicant : 'No promoter'}</div>
                                <div ><button style={{width: '100%'}} className="edit-btn" onClick={() => navigate((project.id).toString())}>Open <FaArrowAltCircleRight/></button></div>
                            </Popup>
                    </Marker>
                );
            })}
        </MapContainer>
    </div>
  );
};

export default ProjectsMap;